import PropTypes from 'prop-types'
import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState
} from 'react'

import trackEvents from '../constants/track-events'
import { trackEvent } from '../helpers/analytics'

// Zwei Contexts, damit Seiten mit openSampleDataDialog (Listen, Settings)
// nicht bei jedem Öffnen und Schließen des Dialogs neu rendern.
const SampleDataActionsContext = createContext(null)
const SampleDataStateContext = createContext(null)

SampleDataActionsContext.displayName = 'SampleDataActionsContext'
SampleDataStateContext.displayName = 'SampleDataStateContext'

const CLOSED = {
  open: false,
  requestId: 0,
  mode: 'remove',
  scope: undefined,
  source: undefined,
  onSuccess: undefined
}

/**
 * Ein Dialog für alle Einstiege (Hinweis, Settings, Stelle löschen, Menüs).
 * Gemountet in Layout, damit er auch in Discover und auf Detailseiten
 * verfügbar ist. Den Dialog selbst rendert Layout (SampleDataDialog).
 */
export function SampleDataProvider({ children }) {
  const [dialog, setDialog] = useState(CLOSED)

  // mode: 'remove' | 'add' | 'info'
  // scope: { career_ids?, space_ids? }, ohne scope gilt alles
  // source: Einstieg fürs Tracking (hint_careers, settings, career_delete …)
  // onSuccess(result): nach erfolgreichem Entfernen oder Hinzufügen
  const openSampleDataDialog = useCallback(
    ({ mode = 'remove', scope, source, onSuccess } = {}) => {
      if (mode === 'remove') {
        trackEvent(trackEvents.SAMPLE_DATA_REMOVE_OPENED, {
          source,
          scoped: !!scope
        })
      }

      setDialog((prev) => ({
        open: true,
        requestId: prev.requestId + 1,
        mode,
        scope,
        source,
        onSuccess
      }))
    },
    []
  )

  const closeSampleDataDialog = useCallback(() => {
    setDialog((prev) => ({ ...prev, open: false }))
  }, [])

  const actions = useMemo(
    () => ({ openSampleDataDialog, closeSampleDataDialog }),
    [openSampleDataDialog, closeSampleDataDialog]
  )

  return (
    <SampleDataActionsContext.Provider value={actions}>
      <SampleDataStateContext.Provider value={dialog}>
        {children}
      </SampleDataStateContext.Provider>
    </SampleDataActionsContext.Provider>
  )
}

SampleDataProvider.propTypes = {
  children: PropTypes.node.isRequired
}

export function useSampleDataDialog() {
  const context = useContext(SampleDataActionsContext)

  if (!context) {
    throw new Error(
      'useSampleDataDialog must be used within a SampleDataProvider'
    )
  }

  return context
}

// Nur für SampleDataDialog
export function useSampleDataDialogState() {
  const context = useContext(SampleDataStateContext)

  if (!context) {
    throw new Error(
      'useSampleDataDialogState must be used within a SampleDataProvider'
    )
  }

  return context
}
